import React, { useState } from 'react';
import FdModal from '../FdModal';
import Form2307Sheet from './Form2307Sheet';
import {
  computeCommission,
  formatCurrency,
  formatDate,
  resolveRoleLabel,
} from './utils';

export default function CommissionModals({
  payRow,
  payForm,
  isSubmitting,
  onPayFormChange,
  onUploadProof,
  onClosePay,
  onSubmitPay,
  cancelRow,
  cancelRemarks,
  onCancelRemarksChange,
  onCloseCancel,
  onSubmitCancel,
  releaseDialog,
  releaseForm,
  selectedCount,
  releaseTotal,
  onReleaseFormChange,
  onCloseRelease,
  onSubmitRelease,
  form2307Row,
  form2307Data,
  onClose2307,
  onDownload2307,
  resolveCommissionRate,
  canTagPaid,
  canCancel,
  canRelease,
}) {
  const [isUploadingProof, setIsUploadingProof] = useState(false);
  const [proofError, setProofError] = useState('');

  async function handleProofChange(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || typeof onUploadProof !== 'function') {
      return;
    }

    setProofError('');
    setIsUploadingProof(true);
    try {
      const result = await onUploadProof(file);
      if (result?.name && result?.url) {
        onPayFormChange('proof_of_payment_name', result.name);
        onPayFormChange('proof_of_payment_url', result.url);
      } else {
        setProofError('Failed to upload proof of payment.');
      }
    } catch (err) {
      setProofError(err?.message || 'Failed to upload proof of payment.');
    } finally {
      setIsUploadingProof(false);
    }
  }

  function removeProof() {
    onPayFormChange('proof_of_payment_name', '');
    onPayFormChange('proof_of_payment_url', '');
  }

  return (
    <>
      <FdModal
        isOpen={Boolean(payRow)}
        onClose={onClosePay}
        eyebrow="Commissions"
        title="Tag Commission as Paid"
        subtitle="Set release reference and payment date for this commission record."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={onClosePay} disabled={isSubmitting}>
              Close
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={onSubmitPay}
              disabled={isSubmitting || isUploadingProof || !canTagPaid || !payForm.reference_number || !payForm.payment_date || !payForm.proof_of_payment_url}
            >
              {isSubmitting ? 'Saving...' : 'Tag as Paid'}
            </button>
          </>
        )}
      >
        {payRow ? (
          <div className="commissions-modal-grid">
            <div className="fd-field fd-field-wide">
              <label>Agent</label>
              <input type="text" value={payRow.agent_name || ''} readOnly />
            </div>
            <div className="fd-field">
              <label>Role / Position</label>
              <input
                type="text"
                value={payRow.agent_position || resolveRoleLabel(payRow.commission_agent_role) || ''}
                readOnly
              />
            </div>
            <div className="fd-field">
              <label>Client</label>
              <input type="text" value={payRow.client_name || ''} readOnly />
            </div>
            <div className="fd-field">
              <label>Reference Number *</label>
              <input
                type="text"
                value={payForm.reference_number}
                onChange={(event) => onPayFormChange('reference_number', event.target.value)}
                placeholder="e.g. CHK-2026-001"
              />
            </div>
            <div className="fd-field">
              <label>Payment Date *</label>
              <input
                type="date"
                value={payForm.payment_date}
                onChange={(event) => onPayFormChange('payment_date', event.target.value)}
              />
            </div>
            <div className="fd-field fd-field-wide">
              <label>Proof of Payment *</label>
              {payForm.proof_of_payment_url ? (
                <div className="commissions-attachment-row">
                  <a href={payForm.proof_of_payment_url} target="_blank" rel="noreferrer">
                    {payForm.proof_of_payment_name || 'View proof of payment'}
                  </a>
                  <button
                    type="button"
                    className="profile-danger-btn profile-danger-btn-xs"
                    onClick={removeProof}
                    disabled={isUploadingProof}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleProofChange}
                  disabled={isUploadingProof}
                />
              )}
              {isUploadingProof ? <span className="commissions-inline-note">Uploading proof of payment...</span> : null}
              {!payForm.proof_of_payment_url && !isUploadingProof ? (
                <span className="commissions-inline-note">The proof of payment file attachment is required.</span>
              ) : null}
              {proofError ? <span className="commissions-danger">{proofError}</span> : null}
            </div>
          </div>
        ) : null}
      </FdModal>

      <FdModal
        isOpen={Boolean(cancelRow)}
        onClose={onCloseCancel}
        eyebrow="Commissions"
        title="Cancel Commission"
        subtitle="Provide cancellation remarks for audit tracking."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={onCloseCancel} disabled={isSubmitting}>
              Close
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary commissions-cancel-btn"
              onClick={onSubmitCancel}
              disabled={isSubmitting || !canCancel || !String(cancelRemarks || '').trim()}
            >
              {isSubmitting ? 'Saving...' : 'Confirm Cancel'}
            </button>
          </>
        )}
      >
        {cancelRow ? (
          <div className="commissions-modal-grid">
            <div className="fd-field fd-field-wide">
              <label>Agent</label>
              <input type="text" value={cancelRow.agent_name || ''} readOnly />
            </div>
            <div className="fd-field fd-field-wide">
              <label>Client</label>
              <input type="text" value={cancelRow.client_name || ''} readOnly />
            </div>
            <div className="fd-field fd-field-wide">
              <label>Cancellation Remarks *</label>
              <textarea
                className="commissions-textarea"
                value={cancelRemarks}
                onChange={(event) => onCancelRemarksChange(event.target.value)}
                placeholder="e.g. Duplicate entry or not eligible for release."
              />
            </div>
          </div>
        ) : null}
      </FdModal>

      <FdModal
        isOpen={releaseDialog}
        onClose={onCloseRelease}
        eyebrow="Commissions"
        title="Release Commissions"
        subtitle="Batch-release selected commission records."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={onCloseRelease} disabled={isSubmitting}>
              Close
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={onSubmitRelease}
              disabled={isSubmitting || !canRelease || !releaseForm.release_reference || !releaseForm.release_date || selectedCount === 0}
            >
              {isSubmitting ? 'Releasing...' : 'Confirm Release'}
            </button>
          </>
        )}
      >
        <div className="commissions-modal-grid">
          <div className="commissions-release-summary">
            <p>Selected Records: <strong>{selectedCount}</strong></p>
            <p>Total Net Commission: <strong>{formatCurrency(releaseTotal)}</strong></p>
          </div>
          <div className="fd-field">
            <label>Release Reference *</label>
            <input
              type="text"
              value={releaseForm.release_reference}
              onChange={(event) => onReleaseFormChange('release_reference', event.target.value)}
              placeholder="e.g. CHK-2026-110"
            />
          </div>
          <div className="fd-field">
            <label>Release Date *</label>
            <input
              type="date"
              value={releaseForm.release_date}
              onChange={(event) => onReleaseFormChange('release_date', event.target.value)}
            />
          </div>
        </div>
      </FdModal>

      <FdModal
        isOpen={Boolean(form2307Row)}
        onClose={onClose2307}
        wide
        eyebrow="Commissions"
        title="BIR Form 2307"
        subtitle="Certificate of Creditable Tax Withheld at Source"
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={onClose2307}>
              Close
            </button>
            <button type="button" className="fd-btn fd-btn-primary" onClick={onDownload2307}>
              Download 2307
            </button>
          </>
        )}
      >
        {form2307Row ? (() => {
          const rate = resolveCommissionRate(form2307Row);
          const calculations = computeCommission(form2307Row.premium_amount, rate);

          return (
            <>
              <div className="commissions-2307-sheet">
                <div className="commissions-2307-header">
                  <p>REPUBLIC OF THE PHILIPPINES</p>
                  <p>BUREAU OF INTERNAL REVENUE</p>
                  <h3>FORM NO. 2307</h3>
                  <span>Certificate of Creditable Tax Withheld at Source</span>
                </div>

                <div className="commissions-2307-grid">
                  <p><strong>Payor:</strong> RMC2 Consultancy</p>
                  <p><strong>Payee:</strong> {form2307Row.agent_name || '-'}</p>
                  <p><strong>Client:</strong> {form2307Row.client_name || '-'}</p>
                  <p><strong>Product:</strong> {form2307Row.product || '-'}</p>
                  <p><strong>Date Withheld:</strong> {formatDate(form2307Row.payment_date)}</p>
                  <p><strong>Agent Position:</strong> {form2307Row.agent_position || '-'}</p>
                </div>

                <table className="commissions-2307-table">
                  <tbody>
                    <tr>
                      <td>Income Payment</td>
                      <td>{formatCurrency(calculations.premium)}</td>
                    </tr>
                    <tr>
                      <td>VAT (12%)</td>
                      <td>{formatCurrency(calculations.vat)}</td>
                    </tr>
                    <tr>
                      <td>Net Commissionable</td>
                      <td>{formatCurrency(calculations.netCommissionable)}</td>
                    </tr>
                    <tr>
                      <td>Commission Rate</td>
                      <td>{(rate * 100).toFixed(2)}%</td>
                    </tr>
                    <tr>
                      <td>Gross Commission</td>
                      <td>{formatCurrency(calculations.grossCommission)}</td>
                    </tr>
                    <tr>
                      <td>10% Withholding Tax (WHT)</td>
                      <td>{formatCurrency(calculations.wtax)}</td>
                    </tr>
                    <tr className="commissions-2307-highlight">
                      <td>Net Commission</td>
                      <td>{formatCurrency(calculations.netCommission)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {form2307Data ? (
                <div className="fd2307-hidden-source" aria-hidden="true">
                  <Form2307Sheet data={form2307Data} rootId="findesk-form-2307-sheet" />
                </div>
              ) : null}
            </>
          );
        })() : null}
      </FdModal>
    </>
  );
}
